import Dropdown from '@components/Dropdown';
import { Input, PasswordInput, PasswordTextField, Textarea, TextField } from '@components/Input';
import { Checkbox, Radio, DatePicker, Upload } from 'antd';
import dayjs from 'dayjs';
import { useField } from 'formik';

const FormField = ({ label, required, error, touched, children, className = '' }) => (
  <div className={`mb-4 ${className}`}>
    {label && (
      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
        {required && <span className="ml-1 text-error">*</span>}
      </label>
    )}
    {children}
    {touched && error && <p className="mt-1 text-xs text-error sm:text-sm">{error}</p>}
  </div>
);

export const FormInput = ({ name, label, required, className, ...props }) => {
  const [field, meta] = useField(name);
  return (
    <Input
      {...field}
      {...props}
      label={label}
      required={required}
      error={meta.error}
      touched={meta.touched}
      className={className}
    />
  );
};

export const FormTextField = ({
  name,
  label,
  required,
  className,
  fieldClassName,
  suffix,
  sanitize,
  ...props
}) => {
  const [field, meta, helpers] = useField(name);
  return (
    <TextField
      {...field}
      {...props}
      label={label}
      required={required}
      error={meta.error}
      touched={meta.touched}
      className={className}
      fieldClassName={fieldClassName}
      suffix={suffix}
      onChange={(event) => {
        const nextValue = sanitize ? sanitize(event.target.value) : event.target.value;
        helpers.setValue(nextValue);
        props.onChange?.(event);
      }}
    />
  );
};

export const FormTextarea = ({ name, label, required, rows = 4, className, ...props }) => {
  const [field, meta] = useField(name);
  return (
    <Textarea
      {...field}
      {...props}
      rows={rows}
      label={label}
      required={required}
      error={meta.error}
      touched={meta.touched}
      className={className}
    />
  );
};

export const FormPassword = ({ name, label, required, className, ...props }) => {
  const [field, meta] = useField(name);
  return (
    <PasswordInput
      {...field}
      {...props}
      label={label}
      required={required}
      error={meta.error}
      touched={meta.touched}
      className={className}
    />
  );
};

export const FormPasswordTextField = ({
  name,
  label,
  required,
  className,
  fieldClassName,
  ...props
}) => {
  const [field, meta, helpers] = useField(name);
  return (
    <PasswordTextField
      {...field}
      {...props}
      label={label}
      required={required}
      error={meta.error}
      touched={meta.touched}
      className={className}
      fieldClassName={fieldClassName}
      onChange={(event) => {
        helpers.setValue(event.target.value);
        props.onChange?.(event);
      }}
    />
  );
};

export const FormSelect = ({ name, label, required, options = [], className, ...props }) => {
  const [field, meta, helpers] = useField(name);
  return (
    <Dropdown
      {...props}
      label={label}
      required={required}
      options={options}
      value={field.value}
      error={meta.error}
      touched={meta.touched}
      className={className}
      onChange={(val) => helpers.setValue(val)}
      onBlur={() => helpers.setTouched(true)}
    />
  );
};

export const FormCheckbox = ({ name, label, children, ...props }) => {
  const [field, meta, helpers] = useField({ name, type: 'checkbox' });
  return (
    <FormField error={meta.error} touched={meta.touched}>
      <Checkbox
        checked={field.value}
        onChange={(e) => helpers.setValue(e.target.checked)}
        {...props}
      >
        {children || label}
      </Checkbox>
    </FormField>
  );
};

export const FormRadioGroup = ({ name, label, required, options = [], ...props }) => {
  const [field, meta, helpers] = useField(name);
  return (
    <FormField label={label} required={required} error={meta.error} touched={meta.touched}>
      <Radio.Group
        value={field.value}
        onChange={(e) => helpers.setValue(e.target.value)}
        options={options}
        {...props}
      />
    </FormField>
  );
};

export const FormDatePicker = ({ name, label, required, ...props }) => {
  const [field, meta, helpers] = useField(name);
  return (
    <FormField label={label} required={required} error={meta.error} touched={meta.touched}>
      <DatePicker
        value={field.value ? dayjs(field.value) : null}
        onChange={(date) => helpers.setValue(date ? date.toISOString() : null)}
        className="w-full"
        status={meta.touched && meta.error ? 'error' : ''}
        {...props}
      />
    </FormField>
  );
};

export const FormUpload = ({ name, label, required, ...props }) => {
  const [, meta, helpers] = useField(name);
  return (
    <FormField label={label} required={required} error={meta.error} touched={meta.touched}>
      <Upload onChange={({ fileList }) => helpers.setValue(fileList)} {...props} />
    </FormField>
  );
};

export default FormInput;
